import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import AudioProvider from '~/features/audio'
import { LinkButton } from '~/features/navigation'
import { calculateProposedScore } from '~/helpers/evaluationScoreHelper'
import { EvaluationCriteriaDisputeMutators } from '~/mutators/evaluation-criteria-disputes'
import { useTRPC } from '~/services/trpc'

import {
  Button,
  Heading1,
  Icon,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@hap/components'
import { EvaluationCriteriaDisputeLabelsRequiringNote } from '@hap/constants'
import { toPercent } from '@hap/utils'

import { CallPlayer } from './-components/call-player/call-player'
import { Criteria, DisputeData } from './-components/criteria'
import { Utterance } from './-components/transcript/utterance'

export const Route = createFileRoute(
  '/_authenticated/$alias/_app/interaction/$contactLogId/dispute/$evaluationId',
)({
  loader: async ({
    context: { trpc, queryClient },
    params: { contactLogId, evaluationId },
  }) => {
    await queryClient.ensureQueryData(
      trpc.interactions.getById.queryOptions(contactLogId),
    )
    await queryClient.ensureQueryData(trpc.evaluations.fromId.queryOptions(evaluationId))
  },
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const router = useRouter()
  const params = Route.useParams()

  const { data: interaction } = useQuery(
    trpc.interactions.getById.queryOptions(params.contactLogId),
  )

  const { data: utterances } = useQuery(
    trpc.utterances.getByContactLogId.queryOptions(params.contactLogId),
  )

  const { data: evaluation } = useQuery(
    trpc.evaluations.fromId.queryOptions(params.evaluationId),
  )

  // Track disputed criteria - Map of criterion ID to dispute data
  const [disputes, setDisputes] = useState<Map<string, DisputeData>>(new Map())

  // Track when to show validation errors (on submit attempt)
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { createBatch, isPending } = EvaluationCriteriaDisputeMutators.useCreateBatch({
    contactLogId: params.contactLogId,
    evaluationId: params.evaluationId,
  })

  // Calculate proposed score based on current disputes
  const proposedScore = useMemo(() => {
    if (!evaluation) return null

    // Flatten criteria from all sections
    const allCriteria = evaluation.sections.flatMap((section) =>
      section.criteria.map((c) => ({
        id: c.id,
        scale: c.scale,
        weight: c.weight ?? 1,
        autoFail: c.autoFail ?? false,
      })),
    )

    const result = calculateProposedScore(allCriteria, evaluation.scores, disputes)
    return result.score
  }, [evaluation, disputes])

  // Handle dispute changes from child criteria components
  const handleDisputeChange = (criterionId: string, dispute: DisputeData | null) => {
    setDisputes((prev) => {
      const next = new Map(prev)
      if (dispute === null) {
        next.delete(criterionId)
      } else {
        next.set(criterionId, dispute)
      }
      return next
    })
  }

  // Check if there are any actual changes (not just selected but unchanged)
  // Also validates that each dispute has a label selected
  const hasChanges = useMemo(() => {
    if (!evaluation || disputes.size === 0) return false

    // Check if any dispute actually changes the score and has a label
    for (const [criterionId, dispute] of disputes) {
      const score = evaluation.scores.find(
        (s) => s.scorecardCriteriaAssignmentId === criterionId,
      )
      // Must have a score change AND a label selected
      if (score && score.awardedPoints !== dispute.proposedAwardedPoints) {
        if (!dispute.label) return false
        // Check if "other" label has a note
        if (
          EvaluationCriteriaDisputeLabelsRequiringNote.includes(dispute.label) &&
          (!dispute.note || dispute.note.trim() === '')
        ) {
          return false
        }
        return true
      }
    }
    return false
  }, [evaluation, disputes])

  // Submit all disputes
  const handleSubmit = async () => {
    // If validation fails, show errors and stop
    if (!hasChanges) {
      setShowValidationErrors(true)
      return
    }

    const disputeArray = Array.from(disputes.entries())
      .map(([criterionId, dispute]) => ({
        scorecardCriteriaAssignmentId: criterionId,
        proposedAwardedPoints: dispute.proposedAwardedPoints,
        note: dispute.note || null,
        label: dispute.label,
      }))
      .filter((d) => {
        // Only include disputes that actually change the score
        const score = evaluation?.scores.find(
          (s) => s.scorecardCriteriaAssignmentId === d.scorecardCriteriaAssignmentId,
        )
        return score && score.awardedPoints !== d.proposedAwardedPoints
      })

    if (disputeArray.length === 0) {
      return
    }

    await createBatch(disputeArray)

    // Navigate back to interaction page
    router.navigate({
      to: '/$alias/interaction/$contactLogId',
      params: { alias: params.alias, contactLogId: params.contactLogId },
    })
  }

  if (!interaction) return <div>interaction not found</div>

  return (
    <div className="bg-accent fixed inset-0 z-20 flex flex-col">
      <header className="flex shrink-0 items-center justify-between border-b px-6 py-2">
        <hgroup>
          <h1 className="font-semibold">Dispute Evaluation</h1>
        </hgroup>
        <div>
          {' '}
          <Tooltip>
            <TooltipTrigger asChild>
              <LinkButton
                to="/$alias/interaction/$contactLogId"
                from="/$alias/interaction/$contactLogId/dispute/$evaluationId"
                variant="outline"
              >
                Cancel
              </LinkButton>
            </TooltipTrigger>
            <TooltipContent>Close</TooltipContent>
          </Tooltip>
        </div>
      </header>
      <div className="flex h-0 shrink grow">
        <div className="flex w-1/2 max-w-200 shrink grow flex-col overflow-y-auto border-r">
          <div className="h-0 shrink grow overflow-y-auto">
            <header className="flex p-6">
              <Heading1 className="shrink grow">Evaluation</Heading1>
            </header>
            <div className="overflow-y-auto p-4">
              <div className="border-border mb-6 rounded-lg border p-4">
                <p className="text-muted-foreground text-sm">
                  Review the evaluation criteria below and select the score you believe is
                  correct. For each disputed criterion, select a reason and optionally add
                  additional context. Your proposed score will be calculated
                  automatically.
                </p>
              </div>
              {evaluation?.sections.map((section) => {
                return (
                  <div
                    key={section.id}
                    className="bg-background border-border-dark mb-4 overflow-hidden rounded-lg py-1"
                  >
                    <header key={section.id} className="border-foreground/60 p-2 px-4">
                      <h3 className="text-accent-foreground">{section.name}</h3>
                    </header>
                    {section.criteria.map((criterion) => {
                      const criterionScore = evaluation.scores.find(
                        (c) => c.scorecardCriteriaAssignmentId === criterion.id,
                      )
                      return (
                        <Criteria
                          key={criterion.id}
                          criterion={criterion}
                          score={criterionScore}
                          onDisputeChange={handleDisputeChange}
                          showValidationErrors={showValidationErrors}
                        />
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
          <div className="flex shrink-0 items-center border-t px-8 py-4">
            <div className="flex grow items-center gap-4">
              <div>
                <h2 className="text-3xl font-bold">{toPercent(evaluation?.score)}</h2>
                <p className="text-muted-foreground text-sm">Original</p>
              </div>
              <Icon name="arrow-right" className="opacity-50" />
              <div>
                <h2 className="text-3xl font-bold">{toPercent(proposedScore)}</h2>
                <p className="text-muted-foreground text-sm">Proposed Score</p>
              </div>
            </div>

            <Button
              variant="lime"
              loading={isPending}
              loadingLabel="Submitting..."
              onClick={handleSubmit}
            >
              Submit for Review
            </Button>
          </div>
        </div>

        <div className="flex w-1/2 shrink grow flex-col">
          <div className="h-0 shrink grow overflow-y-auto">
            <div className="space-y-6 p-6 pt-6">
              <Heading1 className="mb-12">Transcript</Heading1>
              {utterances ? (
                utterances.map((utterance) => (
                  <Utterance
                    key={utterance.id}
                    utterance={utterance}
                    method={interaction.method}
                  />
                ))
              ) : (
                <div className="my-auto flex justify-center">
                  <div className="mt-20 flex flex-col items-center justify-center">
                    <Spinner size="40" />
                    <div className="text-accent-foreground pt-10">Loading Utterances</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <AudioProvider>
            <CallPlayer
              callRecordId={interaction.callRecordId}
              utterances={utterances}
              recordingDuration={interaction.recordingDuration}
            />
          </AudioProvider>
        </div>
      </div>
    </div>
  )
}
